import DairyService from '@src/api/services/dairy';
import React, { useEffect } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { FormFields } from '@src/components/FormFields';
import { GlobalEl } from '@src/styles/app';
import DairyQuery from '@src/QueryHooks/DairyQuery';
import notify from '~/components/Notify';
import { useMutation } from 'react-query';
import Drawer from '~/components/Drawer';
import dayjs from 'dayjs';
import { Form } from 'semantic-ui-react';
import { DateInput } from 'semantic-ui-calendar-react';
import { useTranslation } from 'react-i18next';

interface Props {
  isVisible: boolean;
  handleHiddenDrawer: () => void;
  isLoading?: boolean;
  fetchData?: () => void;
  idTransponderId?: any;
  farmerId: string;
}

const CreateEditTransponderId: React.FunctionComponent<Props> = (props) => {
  const { t } = useTranslation();
  const { isVisible, handleHiddenDrawer, idTransponderId, farmerId } = props;
  const { handleSubmit, control, reset, formState, setValue } = useForm({
    mode: 'onChange',
    defaultValues: {
      uniqueId: '',
      status: false,
      dateFrom: '',
      dateTo: ''
    }
  });

  const dateFrom = useWatch({ control, name: 'dateFrom' });
  const dateTo = useWatch({ control, name: 'dateTo' });

  const getError = (fieldState) => {
    return fieldState?.error ? { content: fieldState.error.message, pointing: 'above' } : false;
  };

  useEffect(() => {
    if (idTransponderId) {
      editTransponderId({ farmerId, id: idTransponderId });
    } else {
      reset({
        uniqueId: '',
        status: false,
        dateFrom: '',
        dateTo: ''
      });
    }
  }, [isVisible, idTransponderId]);

  const onSuccess = () => {
    notify(t('Successfully created transponder ID'), 'success');
    props.fetchData();
  };

  const onTransponderIdSuccess = (response) => {
    const data = response.data;
    reset({
      uniqueId: data.uniqueId || '',
      status: data.status === 1,
      dateFrom: data.dateFrom ? dayjs(data.dateFrom).format('DD.MM.YYYY') : '',
      dateTo: data.dateTo ? dayjs(data.dateTo).format('DD.MM.YYYY') : ''
    });
  };

  const { mutateAsync: storeTransponderId } =
    DairyQuery.storeTransponderId(onSuccess);

  const { mutate: updateTransponderId } = useMutation(
    'transponder-ids-update',
    ({ farmerId, id, data }: { farmerId: string; id: string; data: any }) =>
      DairyService.updateTransponderId(farmerId, id, data),
    {
      onSuccess: () => {
        notify(t('Successfully updated transponder ID'), 'success');
        props.fetchData();
      }
    }
  );

  const { isLoading: isTransponderIdLoading, mutateAsync: editTransponderId } =
    DairyQuery.editTransponderId(onTransponderIdSuccess);

  const onSubmit = (data) => {
    const payload = {
      uniqueId: data.uniqueId,
      status: data.status,
      dateFrom: data.dateFrom ? dayjs(data.dateFrom, 'DD.MM.YYYY').format('YYYY-MM-DD') : null,
      dateTo: data.dateTo ? dayjs(data.dateTo, 'DD.MM.YYYY').format('YYYY-MM-DD') : null
    };

    if (!idTransponderId) {
      storeTransponderId({ farmerId, data: payload });
    } else {
      updateTransponderId({ farmerId, id: idTransponderId, data: payload });
    }
    handleHiddenDrawer();
  };

  const renderContent = () => {
    return (
      <>
        <FormFields.Input
          control={control}
          name='uniqueId'
          label={t('ID')}
          placeholder={t('Enter ID')}
          rules={{
            required: {
              value: true,
              message: t('Please enter ID')
            }
          }}
        />

        <FormFields.Checkbox
          control={control}
          name='status'
          defaultValue={0}
          checkboxValue={1}
          label={t('Status')}
        />

        <Controller
          control={control}
          name='dateFrom'
          render={({ field, fieldState }) => {
            return <Form.Field error={fieldState?.error ? getError(fieldState) : false}>
              <label>{t('Date From')}</label>
              <DateInput
                name='dateFrom'
                placeholder='DD.MM.YYYY'
                closable
                closeOnMouseLeave={false}
                dateFormat='DD.MM.YYYY'
                className='datePicker'
                value={dateFrom}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('dateFrom', value)}
              />
            </Form.Field>
          }}
        />

        <Controller
          control={control}
          name='dateTo'
          rules={{
            validate: {
              afterDateFrom: (value) => {
                if (!value || !dateFrom) return true;
                const dateFromMoment = dayjs(dateFrom, 'DD.MM.YYYY');
                const dateToMoment = dayjs(value, 'DD.MM.YYYY');
                return dateToMoment.isAfter(dateFromMoment) || t('Date To must be after Date From');
              }
            }
          }}
          render={({ field, fieldState }) => {
            return <Form.Field error={fieldState?.error ? getError(fieldState) : false}>
              <label>{t('Date To')}</label>
              <DateInput
                name='dateTo'
                placeholder='DD.MM.YYYY'
                closable
                closeOnMouseLeave={false}
                dateFormat='DD.MM.YYYY'
                className='datePicker'
                value={dateTo}
                iconPosition='left'
                disabled={!dateFrom}
                minDate={dateFrom ? dayjs(dateFrom, 'DD.MM.YYYY').add(1, 'day').toDate() : undefined}
                onChange={(event, { name, value }) => setValue('dateTo', value)}
              />
            </Form.Field>
          }}
        />

        <div style={{ marginTop: '20px' }}>
          <GlobalEl.PrimaryButton
            disabled={!formState.isValid || Object.keys(formState.dirtyFields).length === 0}
          >
            {idTransponderId ? t('Update') : t('Add')}
          </GlobalEl.PrimaryButton>
        </div>
      </>
    );
  };

  return (
    <Drawer
      isVisible={isVisible}
      handleSubmit={handleSubmit(onSubmit)}
      isBusy={isTransponderIdLoading}
      header={idTransponderId ? t('Edit transponder ID') : t('Create transponder ID')}
      closeOnEscape
      onHide={() => {
        reset();
        handleHiddenDrawer();
      }}
      content={renderContent()}
    />
  );
};

export default CreateEditTransponderId;
